import { IconChevronRight, IconMapPin } from '@tabler/icons-react';
import type { RidesCampusView } from '@actschurch/ccms-sdk';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import { EmptyState } from '@/components/ui/EmptyState';
import '@/styles/flows.css';

/** "187 Allan Road, Glen Austin, Midrand" without repeats ("6 1st Avenue, Springs", not "Springs, Springs"). */
export function campusAddressLine(c: RidesCampusView | null | undefined): string {
  if (!c) return '';
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const p of [c.addressLine, c.suburb, c.city]) {
    const v = p?.trim();
    if (!v || seen.has(v.toLowerCase())) continue;
    seen.add(v.toLowerCase());
    parts.push(v);
  }
  return parts.join(', ');
}

interface Props {
  campuses: RidesCampusView[];
  value: number | null;
  onChange: (locationId: number) => void;
  /** The campus chosen on the other side of the route. Hidden with `hideExcluded`, otherwise shown disabled with `excludeNote`. */
  excludeId?: number | null;
  excludeNote?: string;
  /** Leave the other end of the route out of the list instead of showing it disabled. */
  hideExcluded?: boolean;
  /** Accessible name of the list when the page title already asks the question. */
  ariaLabel?: string;
  /** Choosing a campus moves to the next screen: show a chevron instead of a radio circle. */
  navigates?: boolean;
}

/** Big tappable campus cards (name and street address). The same campus can't be both ends of a lift. */
export function CampusPicker({ campuses, value, onChange, excludeId, excludeNote = 'This is your pick-up campus. Choose a different one.', hideExcluded, ariaLabel = 'Campus', navigates }: Props) {
  const active = campuses
    .filter((c) => c.isActive)
    .filter((c) => !(hideExcluded && excludeId != null && c.locationID === excludeId))
    // Campuses an admin ordered come first; the rest (automatic, from Locations) follow A to Z.
    .sort((a, b) => (a.sortOrder > 0 ? a.sortOrder : Number.MAX_SAFE_INTEGER) - (b.sortOrder > 0 ? b.sortOrder : Number.MAX_SAFE_INTEGER) || a.name.localeCompare(b.name));
  if (!active.length) {
    return <EmptyState icon={<IconMapPin size={28} />} title="No campuses are open for lifts yet">Please check back soon.</EmptyState>;
  }
  return (
    <PickerGroup ariaLabel={ariaLabel}>
      {active.map((c) => {
        const disabled = excludeId != null && c.locationID === excludeId;
        const line = campusAddressLine(c) || c.pickupInstructions || undefined;
        return (
          <PickerCard
            key={c.locationID}
            selected={value === c.locationID}
            onSelect={() => onChange(c.locationID)}
            title={c.name}
            description={
              c.isBeta ? (
                <>
                  {line}
                  {line && <br />}
                  <span style={{ color: 'var(--rides-primary)', fontWeight: 600 }}>New to Carpool</span>
                </>
              ) : line
            }
            disabled={disabled}
            disabledNote={excludeNote}
            media={navigates ? <span className="rides-icontile" aria-hidden="true"><IconMapPin size={22} /></span> : undefined}
            hideIndicator={navigates}
            trailing={navigates ? <span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span> : undefined}
          />
        );
      })}
    </PickerGroup>
  );
}
